<template>
  <v-container fluid class="production-stock-page">
    <v-card variant="outlined" class="stock-shell">
      <v-card-title class="stock-header d-flex flex-wrap align-center ga-3 px-4 py-3">
        <v-icon :icon="isRaw ? 'mdi-flask-outline' : 'mdi-package-variant-closed-check'" size="large" />
        <div>
          <div class="text-h6 font-weight-bold">{{ isRaw ? 'Raw Material Stock' : 'Finished Goods Stock' }}</div>
          <div class="text-caption text-medium-emphasis">Production Inventory · Master balances and transaction history</div>
        </div>
        <v-spacer />
        <v-chip size="small" color="primary" variant="tonal">Production Stage</v-chip>
        <v-btn variant="tonal" color="primary" prepend-icon="mdi-refresh" :loading="loading" @click="load">
          Refresh
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-alert type="info" variant="tonal" density="compact" class="mb-4">
          {{ isRaw
            ? 'Balances come from approved material receipts less approved Production consumption and returns. No duplicate Inventory stock is created.'
            : 'Produced Finished Goods are shown here until approved dispatch to Inventory. Once Inventory receives them, they are tracked in the Inventory ledger.' }}
        </v-alert>

        <v-row class="align-center mb-2" dense>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search" label="Search by product, code or batch" placeholder="Search stock masters..."
              density="compact" variant="outlined" prepend-inner-icon="mdi-magnify" clearable hide-details
              @keyup.enter="applyFilters" @click:clear="applyFilters"
            />
          </v-col>
          <v-col v-if="isRaw" cols="12" sm="6" md="3">
            <v-select v-model="itemType" :items="types" label="Material Type" item-title="title" item-value="value"
              density="compact" variant="outlined" hide-details @update:model-value="applyFilters" />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-checkbox v-model="availableOnly" label="Available only" density="compact" hide-details
              @update:model-value="applyFilters" />
          </v-col>
          <v-col cols="12" md="2" class="d-flex justify-md-end ga-2">
            <v-btn color="primary" variant="tonal" prepend-icon="mdi-magnify" @click="applyFilters">Search</v-btn>
          </v-col>
        </v-row>

        <div class="d-flex flex-wrap align-center ga-3 mb-3">
          <v-chip color="blue-grey" variant="tonal" size="small">Masters: {{ total }}</v-chip>
          <span class="text-caption text-medium-emphasis">{{ isRaw ? 'Received, consumed, returned and on-hand' : 'Produced, sent to Inventory and remaining' }} quantities</span>
        </div>

        <v-data-table-server
          :headers="headers" :items="rows" :items-length="total" :loading="loading"
          :items-per-page="perPage" :page="page" item-value="id" density="compact"
          hover class="master-table" @update:options="onOptions"
        >
          <template #item.product="{ item }">
            <div class="py-2">
              <div class="font-weight-bold">{{ item.product_code || '-' }}</div>
              <div class="text-body-2">{{ item.product_name || '-' }}</div>
              <v-chip v-if="isRaw && item.type === 'packaging'" color="indigo" size="x-small" variant="tonal">PM</v-chip>
              <v-chip v-else-if="isRaw" color="teal" size="x-small" variant="tonal">RM</v-chip>
            </div>
          </template>
          <template #item.batch_no="{ item }">
            <div class="font-weight-medium">{{ item.batch_no || '-' }}</div>
            <div class="text-caption text-medium-emphasis">Lot: {{ item.lot_no || '-' }}</div>
          </template>
          <template #item.document="{ item }">
            <div>{{ item.document || '-' }}</div>
            <div class="text-caption text-medium-emphasis">{{ date(item.date) }}</div>
          </template>
          <template #item.in_qty="{ item }"><span class="qty-num">{{ qty(item.in_qty) }}</span></template>
          <template #item.out_qty="{ item }"><span class="qty-num">{{ qty(item.out_qty) }}</span></template>
          <template #item.balance="{ item }">
            <v-chip size="small" variant="tonal" :color="Number(item.balance) > 0 ? 'success' : 'default'">
              {{ qty(item.balance) }} {{ item.unit || '' }}
            </v-chip>
          </template>
          <template #item.unit_cost="{ item }">{{ money(item.unit_cost, 4) }}</template>
          <template #item.valuation="{ item }"><strong>{{ money(item.valuation) }}</strong></template>
          <template #item.actions="{ item }">
            <v-btn variant="tonal" color="primary" size="small" prepend-icon="mdi-text-box-search-outline"
              @click="openDetails(item)">Details &amp; Logs</v-btn>
          </template>
          <template #no-data>
            <div class="py-8 text-center text-medium-emphasis">No posted stock records match the selected filters.</div>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <v-dialog v-model="detailsOpen" max-width="1150" scrollable>
      <v-card class="details-dialog">
        <v-card-title class="d-flex flex-wrap align-center ga-2 dialog-heading px-4 py-3">
          <v-icon icon="mdi-clipboard-text-clock-outline" />
          <span>Stock Master &amp; Movement Logs</span>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" size="small" @click="detailsOpen = false" />
        </v-card-title>
        <v-card-text class="pa-4">
          <v-skeleton-loader v-if="loadingDetails" type="article, table" />
          <template v-else-if="selected">
            <div class="mb-4">
              <div class="text-subtitle-1 font-weight-bold">{{ selected.product_code }} — {{ selected.product_name }}</div>
              <div class="text-caption text-medium-emphasis">
                Batch {{ selected.batch_no || '-' }} · Lot {{ selected.lot_no || '-' }} · Source {{ selected.document || '-' }}
              </div>
            </div>
            <v-row dense class="mb-4">
              <v-col cols="6" sm="3"><Stat label="Received / Produced" :value="`${qty(selected.in_qty)} ${selected.unit || ''}`" /></v-col>
              <v-col cols="6" sm="3"><Stat :label="isRaw ? 'Consumed / Returned' : 'Sent to Inventory'" :value="`${qty(selected.out_qty)} ${selected.unit || ''}`" /></v-col>
              <v-col cols="6" sm="3"><Stat label="Production Balance" :value="`${qty(selected.balance)} ${selected.unit || ''}`" /></v-col>
              <v-col cols="6" sm="3"><Stat label="Balance Valuation" :value="money(selected.valuation)" /></v-col>
            </v-row>
            <v-row v-if="!isRaw" dense class="mb-3">
              <v-col cols="6" sm="3"><Stat label="Raw Material Cost" :value="money(selected.rm_cost)" /></v-col>
              <v-col cols="6" sm="3"><Stat label="Packaging Cost" :value="money(selected.packaging_cost)" /></v-col>
              <v-col cols="6" sm="3"><Stat label="Labour Cost" :value="money(selected.labour_cost)" /></v-col>
              <v-col cols="6" sm="3"><Stat label="Overhead Cost" :value="money(selected.overhead_cost)" /></v-col>
            </v-row>
            <v-row v-else dense class="mb-3">
              <v-col cols="6" sm="3"><Stat label="Purchase Purity" :value="`${qty(selected.purchase_purity)}%`" /></v-col>
              <v-col cols="6" sm="3"><Stat label="Unit Cost" :value="money(selected.unit_cost, 4)" /></v-col>
              <v-col cols="6" sm="3"><Stat label="Consumed Qty" :value="qty(selected.consumed_qty)" /></v-col>
              <v-col cols="6" sm="3"><Stat label="Returned Qty" :value="qty(selected.returned_qty)" /></v-col>
            </v-row>
            <v-alert type="info" variant="tonal" density="compact" class="mb-3">{{ detailsNote }}</v-alert>
            <div class="text-subtitle-1 font-weight-bold mb-2">Movement History</div>
            <v-table density="compact" class="logs-table">
              <thead><tr>
                <th>Date</th><th>Movement</th><th>Source Document</th>
                <th class="text-end">IN</th><th class="text-end">OUT</th>
                <th class="text-end">Rate</th><th class="text-end">Cost</th>
              </tr></thead>
              <tbody>
                <tr v-for="log in logs" :key="log.id">
                  <td>{{ date(log.date) }}</td>
                  <td><v-chip size="x-small" :color="log.in_qty > 0 ? 'success' : 'warning'" variant="tonal">{{ log.type }}</v-chip></td>
                  <td>{{ log.document || '-' }}</td>
                  <td class="text-end">{{ log.in_qty ? qty(log.in_qty) : '-' }}</td>
                  <td class="text-end">{{ log.out_qty ? qty(log.out_qty) : '-' }}</td>
                  <td class="text-end">{{ money(log.unit_cost, 4) }}</td>
                  <td class="text-end">{{ money(log.amount) }}</td>
                </tr>
                <tr v-if="!logs.length"><td colspan="7" class="text-center py-5">No posted movement records found.</td></tr>
              </tbody>
            </v-table>
            <div v-if="!isRaw" class="text-caption text-medium-emphasis mt-2">
              Inventory approved receipt: {{ qty(selected.inventory_received_quantity || 0) }} {{ selected.unit || '' }}.
              This is trace information; it does not increase Production-stage balance.
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, ref } from 'vue';
import Axios from 'axios';
import { toast } from 'vue3-toastify';
import { productionInventoryStock } from '@/store/api';

const props = defineProps({ kind: { type: String, required: true } });
const isRaw = computed(() => props.kind === 'raw');
const types = [
  { title: 'All Materials (RM & PM)', value: '' },
  { title: 'Raw Materials', value: 'raw_material' },
  { title: 'Packaging Materials', value: 'packaging' },
];
const search = ref('');
const itemType = ref('');
const availableOnly = ref(false);
const page = ref(1);
const perPage = ref(25);
const rows = ref([]);
const total = ref(0);
const loading = ref(false);
const loadingDetails = ref(false);
const detailsOpen = ref(false);
const selected = ref(null);
const detailsNote = ref('');
const logs = ref([]);
let requestVersion = 0;

const headers = computed(() => [
  { title: 'Product', key: 'product', sortable: false, minWidth: '230px' },
  { title: 'Batch / Lot', key: 'batch_no', sortable: false, minWidth: '140px' },
  { title: 'Source Document', key: 'document', sortable: false, minWidth: '145px' },
  { title: isRaw.value ? 'Received' : 'Produced', key: 'in_qty', align: 'end', sortable: false },
  { title: isRaw.value ? 'Consumed / Returned' : 'Sent', key: 'out_qty', align: 'end', sortable: false },
  { title: 'Balance', key: 'balance', align: 'end', sortable: false },
  { title: 'Unit Cost', key: 'unit_cost', align: 'end', sortable: false },
  { title: 'Value', key: 'valuation', align: 'end', sortable: false },
  { title: 'Details', key: 'actions', align: 'center', sortable: false },
]);

const Stat = defineComponent({
  props: { label: String, value: [String, Number] },
  setup(props) {
    return () => h('div', { class: 'stock-stat' }, [
      h('div', { class: 'stock-stat-label' }, props.label),
      h('div', { class: 'stock-stat-value' }, String(props.value ?? '-')),
    ]);
  },
});

async function load() {
  const version = ++requestVersion;
  loading.value = true;
  try {
    const response = await Axios.get(`${productionInventoryStock}/${props.kind}`, {
      params: {
        page: page.value, per_page: perPage.value, search: search.value?.trim() || undefined,
        item_type: isRaw.value ? itemType.value || undefined : undefined,
        available_only: availableOnly.value ? 1 : undefined,
      },
    });
    if (version !== requestVersion) return;
    const data = response.data.data;
    rows.value = data?.data || [];
    total.value = Number(data?.total || 0);
  } catch (error) {
    if (version !== requestVersion) return;
    rows.value = [];
    total.value = 0;
    toast.error(error?.response?.data?.message || 'Could not load production inventory stock.');
  } finally {
    if (version === requestVersion) loading.value = false;
  }
}

function onOptions(options) {
  if (options.page === page.value && options.itemsPerPage === perPage.value && requestVersion > 0) return;
  page.value = options.page;
  perPage.value = options.itemsPerPage;
  load();
}
function applyFilters() {
  page.value = 1;
  load();
}
async function openDetails(item) {
  detailsOpen.value = true;
  selected.value = item;
  logs.value = [];
  detailsNote.value = '';
  loadingDetails.value = true;
  try {
    const response = await Axios.get(`${productionInventoryStock}/${props.kind}/${item.id}`);
    if (!detailsOpen.value || selected.value?.id !== item.id) return;
    selected.value = response.data.data.master;
    logs.value = response.data.data.logs || [];
    detailsNote.value = response.data.data.note || '';
  } catch (error) {
    toast.error(error?.response?.data?.message || 'Could not load stock movement history.');
  } finally {
    if (selected.value?.id === item.id) loadingDetails.value = false;
  }
}
function qty(value) { return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 4 }); }
function money(value, digits = 2) {
  return Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: digits });
}
function date(value) { return value ? String(value).slice(0, 10) : '-'; }

onMounted(load);
</script>

<style scoped>
.stock-shell { border-radius: 12px; overflow: hidden; }
.stock-header, .dialog-heading { background: #eaf0f4; border-bottom: 1px solid #d7e1e7; }
.master-table { border: 1px solid #e5e7eb; border-radius: 8px; overflow: auto; }
.master-table :deep(th) { background: #f1f4f6; white-space: nowrap; font-weight: 700; }
.master-table :deep(td) { white-space: nowrap; }
.qty-num { font-variant-numeric: tabular-nums; }
.stock-stat { border: 1px solid #e2e8f0; border-radius: 9px; padding: 12px; min-height: 72px; background: #f8fafc; }
.stock-stat-label { font-size: .75rem; color: #64748b; }
.stock-stat-value { font-size: .96rem; font-weight: 700; margin-top: 5px; }
.logs-table { border: 1px solid #d9e1e8; border-radius: 8px; overflow: auto; }
.logs-table :deep(th) { background: #eff2f5; font-weight: 700; white-space: nowrap; }
.logs-table :deep(td) { white-space: nowrap; }
@media (max-width: 600px) { .production-stock-page { padding: 8px; } }
</style>
